<script>
    // main dressing export that includes the api
    import dressing from "@intellectability/platform-common";

    // common constants
    import {ACTIONS} from "@intellectability/platform-common/constants";
    // common components
    import {
        ExportButtons,
        FlexList,
        Icon,
        SuperFilter,
        userSecurity
    } from "@intellectability/platform-common/sv";
    import {create_security_error, actionCheck} from "@intellectability/platform-common/utils";

    import {onMount} from "svelte";

    // bootstrap
    import {
        Breadcrumb,
        BreadcrumbItem,
        Dropdown,
        DropdownItem,
        DropdownMenu,
        DropdownToggle,
        Input,
        Label
    } from "@sveltestrap/sveltestrap";

    // import {TrainingSchedulerModal} from "./lib/TrainingSchedulerModal/index.js";

    const {USER_DETAILS_VIEW, USERS_LIST, USER_TRAINING_SCHEDULER_VIEW, USER_TRAINING_SCHEDULER_EDIT} = ACTIONS;

    let hasAccess = $derived(actionCheck(userSecurity.userActions));

    let loading = $state(true);

    const canViewUserTrainingScheduler = $derived(
        hasAccess([USER_TRAINING_SCHEDULER_VIEW, USER_TRAINING_SCHEDULER_EDIT])
    );

    const canEditUserTrainingScheduler = $derived(hasAccess(USER_TRAINING_SCHEDULER_EDIT));

    const applicable_business_rule_names = [
        "show_hire_date_in_superfilter"
    ];

    let applicable_business_rules = $derived.by(function () {
        return userSecurity.businessRules.filter(function (rule) {
            return applicable_business_rule_names.includes(rule.rule_name);
        });
    });

    // flexlist data
    let users = $state.raw([]);

    // for dropdowns
    let userRegions = $state.raw([]);
    let userRoles = $state.raw([]);
    let userStatuses = $state.raw([]);

    // filter options
    let selectedFirstName = $state("");
    let selectedLastName = $state("");
    let selectedTitle = $state("");
    let selectedEmployer = $state("");
    let selectedPhone = $state("");
    let selectedRegion = $state("");
    let selectedStatus = $state("");
    let selectedRole = $state("");
    let selectedScope = $state("");
    let selectedHiredAfter = $state("");
    let selectedHiredBefore = $state("");

    let columns = $derived([
        {
            name: "Last Name",
            field: "last_name",
            width: "",
            sortedBy: sort.field === "last_name" ? sort.order : "",
            sortable: true,
            collapse: false,
            filterable: true,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedLastName = value;
            }
        },
        {
            name: "First Name",
            field: "first_name",
            width: "",
            sortedBy: sort.field === "first_name" ? sort.order : "",
            sortable: true,
            collapse: false,
            filterable: true,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedFirstName = value;
            }
        },
        {
            name: "Title",
            field: "title",
            width: "",
            sortedBy: sort.field === "title" ? sort.order : "",
            sortable: true,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedTitle = value;
            }
        },
        {
            name: "Employer",
            field: "employer",
            width: "",
            sortable: true,
            sortedBy: sort.field === "employer" ? sort.order : "",
            collapse: false,
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedEmployer = value;
            }
        },
        {
            name: "Phone",
            field: "phone",
            width: "",
            sortable: true,
            sortedBy: sort.field === "phone" ? sort.order : "",
            filterType: "text",
            filterPlaceholder: "Search",
            onfilter: function (value) {
                selectedPhone = value;
            }
        },
        {
            name: "Region",
            field: "region",
            width: "",
            sortable: true,
            sortedBy: sort.field === "region" ? sort.order : "",
            filterType: "select",
            filterOptions: userRegionOptions,
            onfilter: function (value) {
                selectedRegion = value;
            }
        },
        {
            name: "Status",
            field: "status",
            width: "",
            sortable: true,
            sortedBy: sort.field === "status" ? sort.order : "",
            filterType: "select",
            filterOptions: userStatusOptions,
            onfilter: function (value) {
                selectedStatus = value;
            }
        }
    ]);

    // pagination
    let pagination = $state({
        show: true,
        page: 1,
        limit: 10,
        recordsFiltered: null,
        recordsTotal: null,
        pageLengthMenu: [10, 25, 50, 100, 500]
    });

    // sort
    let sort = $state({
        field: "last_name",
        order: "asc"
    });

    // column filters take the string
    let userRegionOptions = $derived(userRegions.map(function (region) {
        return {name: region.reg_name, value: region.reg_name};
    }));

    let userStatusOptions = $derived(userStatuses.map(function (status) {
        return {name: status.status, value: status.status};
    }));

    let userRoleOptions = $derived(userRoles.map(function (role) {
        return {name: role.role_name, value: role.role_id};
    }));

    let userScopeOptions = [
        {
            value: "1",
            name: "Case Load"
        },
        {
            value: "2",
            name: "Provider"
        },
        {
            value: "3",
            name: "Region"
        },
        {
            value: "4",
            name: "State"
        }
    ];

    onMount(function load() {

        // user has access to the user list
        if (!hasAccess(USERS_LIST)) {
            const err = create_security_error("UserList.svelte", "UserList", USERS_LIST);
            dressing.api.report_security_error(err)
                .then(() => dressing.api.logout());

            return;
        }

        const needed_initial_data = [
            dressing.get("/application/regions").then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {regions} = data;
                    if (Array.isArray(regions) && regions.length > 0) {
                        userRegions = regions;
                    }
                }
            }),
            dressing.get("/users/roles").then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {users_roles} = data;
                    if (Array.isArray(users_roles) && users_roles.length > 0) {
                        userRoles = users_roles;
                    }
                }
            }),
            dressing.get("/users/statuses").then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {users_statuses} = data;
                    if (Array.isArray(users_statuses) && users_statuses.length > 0) {
                        userStatuses = users_statuses;
                    }
                }
            })
        ];

        Promise.allSettled(needed_initial_data)
            .then(function () {
                return loading = false;
            });
    });

    $effect(function filter_list() {
        if (!loading) {
            const filter_query = {
                first_name: selectedFirstName || null,
                last_name: selectedLastName || null,
                title: selectedTitle || null,
                employer: selectedEmployer || null,
                phone: selectedPhone || null,
                region: selectedRegion || null,
                status: selectedStatus || null,
                role: selectedRole || null,
                scope: selectedScope || null,
                hired_after: selectedHiredAfter || null,
                hired_before: selectedHiredBefore || null
            };

            const sort_query = {
                sort: sort.field || null,
                order: sort.order || null
            };

            const page_query = {
                page: pagination.page || null,
                limit: pagination.limit || null
            };

            dressing.get("/users", {
                params: Object.assign(filter_query, {...sort_query, ...page_query})
            }).then(({data: res}) => {
                const {success, data} = res;
                if (success === true) {
                    const {users: u} = data;
                    if (Array.isArray(u)) {
                        users = u;
                        pagination.recordsFiltered = data.recordsFiltered;
                        pagination.recordsTotal = data.recordsTotal;
                    }
                }
            });
        }
    });

    function row_selected(row) {
        if (hasAccess(USER_DETAILS_VIEW)) {
            window.location.href = `/index.php?action=${USER_DETAILS_VIEW}&uid=${row.id}`;
        }
    }
</script>

<!-- Breadcrumbs -->
<Breadcrumb divider={" "} class="breadcrumbs mb-3">
    <BreadcrumbItem>
        <a href="/index.php?action=30000">
            <Icon icon="user" class="mr-1"/>
            users
        </a>
    </BreadcrumbItem>
    <BreadcrumbItem class="mx-3">
        <Icon icon="chevron-right"/>
    </BreadcrumbItem>
    <BreadcrumbItem>
        list
    </BreadcrumbItem>
</Breadcrumb>
<!-- Page Title -->
<h1>
    <Icon icon="user" class="me-2"/>
    Users
</h1>
<!-- Super Filter -->
<div class="mb-4">
    <SuperFilter classes="row px-0 mx-0" onreset={() => {
                   selectedRole = "";
                   selectedScope = "";
                }}>
        <div class="col-12 col-md-3">
            <Label class="w-100">
                Role
                <Dropdown direction="down">
                    <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {userRoleOptions.find(function (role) {
                                            return role.value === selectedRole;
                                        })?.name || "All"}
                                    </span>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onclick={() => selectedRole = ""}>
                            All
                        </DropdownItem>
                        {#each userRoleOptions as {name, value}}
                            <DropdownItem
                                    class={selectedRole === value ? "active" : ""}
                                    onclick={() => selectedRole = value}>
                                {name}
                            </DropdownItem>
                        {/each}
                    </DropdownMenu>
                </Dropdown>
            </Label>
        </div>
        <div class="col-12 col-md-3">
            <Label class="w-100">
                Scope
                <Dropdown direction="down">
                    <DropdownToggle color="transparent" class="w-100" caret>
                                    <span>
                                        {userScopeOptions.find(scope => {
                                            return scope.value === selectedScope;
                                        })?.name || "All"}
                                    </span>
                    </DropdownToggle>
                    <DropdownMenu>
                        <DropdownItem onclick={() => selectedScope = ""}>
                            All
                        </DropdownItem>
                        {#each userScopeOptions as {name, value}}
                            <DropdownItem
                                    class={selectedScope === value ? "active" : ""}
                                    onclick={() => selectedScope = value}>
                                {name}
                            </DropdownItem>
                        {/each}
                    </DropdownMenu>
                </Dropdown>
            </Label>
        </div>
        {#each applicable_business_rules as rule}
            {#if rule.rule_name === "show_hire_date_in_superfilter"}
                <div class="col-12 col-md-2">
                    <Label>
                        Hired on or after
                        <Input class="w-100" type="date" bind:value={selectedHiredAfter}/>
                    </Label>
                </div>
                <div class="col-12 col-md-2">
                    <Label>
                        Hired on or before
                        <Input class="w-100" type="date" bind:value={selectedHiredBefore}/>
                    </Label>
                </div>
            {/if}
        {/each}
    </SuperFilter>
</div>

<!--{#if canViewUserTrainingScheduler || canEditUserTrainingScheduler}-->
<!--    <TrainingSchedulerModal/>-->
<!--{/if}-->

<!-- Export Buttons -->
<div class="d-flex w-100 justify-content-end mb-3">

    <ExportButtons show={["excel", "pdf"]}
                   href="ajax/default/ajax.download_list.php?list=30000"/>
</div>
<FlexList
        loading={loading}
        pagerConfig={pagination}
        onpagechange={(page) => pagination.page = page}
        onsort={(field, order) => {sort = {field, order};}}
        hoverable={hasAccess(USER_DETAILS_VIEW)}
        onselection={row_selected}
        onpagelengthchange={(newLimit) => pagination.limit = newLimit}
        {columns}
        data={users}>
</FlexList>
